import { computed, ref, watch } from 'vue';
import {
  cloneColumnFilter,
  cloneFilterGroup,
  countGroupConditions,
  normalizeColumnFilter,
  restrictedFilterOperators,
  type BaseFilterMode,
  type NxTableColumnFilter,
  type NxTableFilterColumnMeta,
  type NxTableFilterGroup,
  type TableColumnConfig,
  type TableState,
} from '../internal';
import { useSavedTableFilters } from './useSavedTableFilters';
import { useTableFilter } from './useTableFilter';

type FilterValues<T extends Record<string, unknown>> = Required<TableState<T>>['filterValues'];

/**
 * Everything a table filters by (I-2172, out of NxTable.vue): the classic quick
 * filters of the search row, the structured column filters of the header
 * popovers, the wizard's advanced tree, the consumer's base filter, and the
 * named filter sets. The table never filters rows itself — every change asks
 * `onFilterChange` to reload from the first page.
 */
export function useTableFilterState<T extends Record<string, unknown>>(params: {
  columnConfigs: { readonly value: ReadonlyMap<string, Omit<TableColumnConfig<T>, 'id'>> };
  columnOrder: { readonly value: readonly string[] };
  /** The loaded rows — the value source of a string column's checklist. */
  data: () => T[];
  baseFilter: () => FilterValues<T> | undefined;
  baseFilterMode: () => BaseFilterMode;
  /** The table's own settings key; its named filter sets live there (see `useSavedTableFilters`). */
  settingsKey: () => string | undefined;
  onFilterChange: () => void;
}) {
  const { columnConfigs, columnOrder, onFilterChange } = params;
  const { filterModifier, filterInfo, onFilter } = useTableFilter<T>({
    triggerChange: onFilterChange,
  });

  const hasFilters = computed(() =>
    columnOrder.value.some((columnId) => columnConfigs.value.get(columnId)!.filterable)
  );

  // ---- Structured filters (header popover conditions + wizard tree) ----
  const columnFilters = ref(new Map<string, NxTableColumnFilter>());
  const advancedFilter = ref<NxTableFilterGroup | null>(null);
  /** Wizard tree waiting for a name in the save dialog (applied on save). */
  const pendingWizardTree = ref<NxTableFilterGroup | null>(null);

  /**
   * Labels of row-derived checklist values (value → display), per column —
   * filled by rowDerivedValues so ids picked from loaded rows still show their
   * display text in checklist and chips.
   */
  const rowValueLabels = ref(new Map<string, Map<string, string>>());

  /**
   * Checklist values from the loaded rows: raw cell value (e.g. FK id) as the
   * filter value, `toCsvString` as its label. Object-valued cells fall back to
   * the display string entirely.
   */
  function rowDerivedValues(columnId: string, search: string): string[] {
    const config = columnConfigs.value.get(columnId);
    if (!config) return [];
    const labels = new Map<string, string>();
    params.data().forEach((row, rowIndex) => {
      const raw = (row as Record<string, unknown>)[columnId];
      const display = config.toCsvString(row, rowIndex);
      const value =
        raw === null || raw === undefined || typeof raw === 'object' ? display : String(raw);
      if (value === '') return;
      labels.set(value, display !== '' ? display : value);
    });
    rowValueLabels.value.set(columnId, labels);
    const query = search.toLocaleLowerCase();
    return [...labels.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .filter(([, label]) => !query || label.toLocaleLowerCase().includes(query))
      .map(([value]) => value);
  }

  /** Column metadata for the filter UIs (popover, chips, wizard). */
  function filterColumnMeta(columnId: string): NxTableFilterColumnMeta {
    const config = columnConfigs.value.get(columnId);
    return {
      columnId,
      displayName: (config?.displayName ?? columnId) as string,
      kind: config?.filterKind ?? 'string',
      options: config?.filterOptions,
      searchable: !config?.filterSearchHidden,
      operators: config?.filterOperators,
      modifiers: config?.filterModifiers,
      optionLabel: (value: string) => {
        const own = config?.filterOptionLabel?.(value);
        if (own !== undefined && own !== value) return own;
        return rowValueLabels.value.get(columnId)?.get(value) ?? own ?? value;
      },
    };
  }

  const filterableColumnMeta = computed<NxTableFilterColumnMeta[]>(() =>
    columnOrder.value
      .filter((columnId) => columnConfigs.value.get(columnId)!.filterable)
      .map(filterColumnMeta)
      // A column restricted to the checklist (`filterOperators: ['oneOf']`) has no
      // condition the wizard could build.
      .filter((meta) => restrictedFilterOperators(meta.kind, meta.operators).length > 0)
  );

  /**
   * Checklist value source of a column. Consumer-provided `filterValueSource`
   * wins (GraphQL seam, e.g. relation columns searching ALL records); string
   * columns fall back to the distinct display values of the loaded rows
   * ("everything currently visible"); static select columns use their options
   * directly (no source).
   */
  function filterValueSourceFor(
    columnId: string
  ): ((search: string) => string[] | Promise<string[]>) | undefined {
    const config = columnConfigs.value.get(columnId);
    if (!config) return undefined;
    if (config.filterValueSource) {
      const custom = config.filterValueSource;
      // Custom (server) source first; on an empty result or an error fall back
      // to the loaded rows so the checklist is never blank.
      return async (search: string) => {
        try {
          const values = await custom(search);
          if (values.length > 0) return values;
        } catch {
          /* endpoint unavailable → row fallback */
        }
        return rowDerivedValues(columnId, search);
      };
    }
    if ((config.filterKind ?? 'string') !== 'string') return undefined;
    return (search: string) => rowDerivedValues(columnId, search);
  }

  const hasActiveFilters = computed(
    () =>
      [...filterInfo.value.values()].some((values) => values.length > 0) ||
      [...columnFilters.value.values()].some((filter) => normalizeColumnFilter(filter)) ||
      countGroupConditions(advancedFilter.value) > 0
  );

  /**
   * Popover apply with a legacy bridge: SIMPLE shapes (value picks, text
   * contains, single equals) are written into the classic quick `filterValues`,
   * which every existing server consumer (REST/GraphQL grids) already
   * translates — so the popover filters real tables today. Structured shapes
   * (operators, ranges, multi conditions, exclude) stay in `columnFilters`, and
   * so does ONE checked value of a text column: as a quick value it could not be
   * told apart from a typed search term, so it would filter by "contains" and
   * reopen as that search instead of as the checked value (BLP/nx#3240).
   */
  function applyHeaderFilter(columnId: string, filter: NxTableColumnFilter | null) {
    const config = columnConfigs.value.get(columnId);
    const kind = config?.filterKind ?? 'string';
    const setQuick = (values: string[]) => {
      if (values.length > 0) {
        filterInfo.value.set(columnId, values as (T[keyof T] | string)[]);
      } else {
        filterInfo.value.delete(columnId);
      }
      config?.onQueryLoaded?.(values);
    };
    const single = filter && filter.conditions.length === 1 ? filter.conditions[0] : undefined;
    const oneTextPick =
      single?.operator === 'oneOf' && kind === 'string' && single.values?.length === 1;
    if (!filter) {
      setQuick([]);
      columnFilters.value.delete(columnId);
    } else if (single?.operator === 'oneOf' && (single.values?.length ?? 0) > 0 && !oneTextPick) {
      setQuick([...single.values!]);
      columnFilters.value.delete(columnId);
    } else if (
      single?.operator === 'contains' &&
      kind === 'string' &&
      (single.value ?? '') !== ''
    ) {
      setQuick([single.value!]);
      columnFilters.value.delete(columnId);
    } else if (
      single?.operator === 'equals' &&
      (single.value ?? '') !== '' &&
      kind !== 'string' &&
      kind !== 'select'
    ) {
      setQuick([single.value!]);
      columnFilters.value.delete(columnId);
    } else {
      setQuick([]);
      columnFilters.value.set(columnId, filter);
    }
    onFilterChange();
  }

  function removeQuickFilter(columnId: string) {
    filterInfo.value.delete(columnId);
    columnConfigs.value.get(columnId)?.onQueryLoaded?.([]);
    onFilterChange();
  }

  function removeColumnCondition(columnId: string, index: number) {
    const filter = columnFilters.value.get(columnId);
    if (!filter) return;
    const conditions = filter.conditions.filter((_, i) => i !== index);
    if (conditions.length === 0) columnFilters.value.delete(columnId);
    else
      columnFilters.value.set(columnId, {
        modifier: filter.modifier,
        conditions,
      });
    onFilterChange();
  }

  function applyAdvancedFilter(tree: NxTableFilterGroup | null) {
    advancedFilter.value = tree;
    onFilterChange();
  }

  /** Drops every filter and clears the search row's cells — without reloading. */
  function clearFilterState() {
    filterModifier.value = 'and';
    filterInfo.value = new Map();
    columnFilters.value = new Map();
    advancedFilter.value = null;
    columnOrder.value.forEach((columnId) => columnConfigs.value.get(columnId)!.onQueryLoaded?.([]));
  }

  function resetAllFilters() {
    clearFilterState();
    onFilterChange();
  }

  /**
   * Quick filters from a stored table state (`setTableState`). A column the map
   * leaves out is an empty filter, so its search cell is cleared — it used to be
   * handed `undefined`, which the built-in column handlers cannot read, and the
   * exception stopped the rest of the state from being restored (I-2133). The
   * map is copied: the table edits its filters in place and must not write into
   * the caller's object.
   */
  function restoreQuickFilters(values: FilterValues<T>) {
    filterInfo.value = new Map(values) as FilterValues<T>;
    columnOrder.value.forEach((columnId) =>
      columnConfigs.value.get(columnId)!.onQueryLoaded?.(filterInfo.value.get(columnId) ?? [])
    );
  }

  // ---- Named filter sets ----
  const { savedFilters, saveFilter, deleteFilter } = useSavedTableFilters({
    settingsKey: params.settingsKey,
  });

  function saveNamedFilter(name: string) {
    const tree = pendingWizardTree.value ?? advancedFilter.value;
    if (pendingWizardTree.value) {
      advancedFilter.value = pendingWizardTree.value;
      pendingWizardTree.value = null;
      onFilterChange();
    }
    saveFilter({
      name,
      filterModifier: filterModifier.value,
      quickFilters: Object.fromEntries(
        [...filterInfo.value.entries()]
          .filter(([, values]) => values.length > 0)
          .map(([columnId, values]) => [columnId, values.map(String)])
      ),
      columnFilters: Object.fromEntries(
        [...columnFilters.value.entries()].map(([columnId, filter]) => [
          columnId,
          cloneColumnFilter(filter),
        ])
      ),
      advancedFilter: tree ? cloneFilterGroup(tree) : null,
    });
  }

  function applySavedFilter(id: string) {
    const saved = savedFilters.value.find((f) => f.id === id);
    if (!saved) return;
    filterModifier.value = saved.filterModifier;
    filterInfo.value = new Map(
      Object.entries(saved.quickFilters).map(([columnId, values]) => [columnId, [...values]])
    ) as FilterValues<T>;
    columnFilters.value = new Map(
      Object.entries(saved.columnFilters).map(([columnId, filter]) => [
        columnId,
        cloneColumnFilter(filter),
      ])
    );
    advancedFilter.value = saved.advancedFilter ? cloneFilterGroup(saved.advancedFilter) : null;
    // Repaint every quick filter cell (set or cleared).
    columnOrder.value.forEach((columnId) =>
      columnConfigs.value.get(columnId)!.onQueryLoaded?.(saved.quickFilters[columnId] ?? [])
    );
    onFilterChange();
  }

  // ---- Base filter (the consumer's own, always applied) ----
  const baseFilterValues = computed(() => params.baseFilter() ?? (new Map() as FilterValues<T>));
  /** Columns a `lock` base filter owns: the user cannot filter them. */
  const lockedColumns = computed(() => {
    const base = params.baseFilter();
    if (params.baseFilterMode() !== 'lock' || !base) return new Set<string>();
    return new Set([...base.keys()] as string[]);
  });
  watch(
    params.baseFilter,
    (newBase, oldBase) => {
      if (params.baseFilterMode() === 'lock' && newBase) {
        for (const columnId of newBase.keys()) {
          if (filterInfo.value.has(columnId as string)) {
            filterInfo.value.delete(columnId as string);
            columnConfigs.value.get(columnId as string)?.onQueryLoaded?.([]);
          }
          columnFilters.value.delete(columnId as string);
        }
      }
      // refetch when base filter identity changes (consumers should pass a new Map)
      if (newBase !== oldBase) onFilterChange();
    },
    { deep: true }
  );

  return {
    filterModifier,
    filterInfo,
    onFilter,
    hasFilters,
    columnFilters,
    advancedFilter,
    pendingWizardTree,
    filterColumnMeta,
    filterableColumnMeta,
    filterValueSourceFor,
    hasActiveFilters,
    applyHeaderFilter,
    removeQuickFilter,
    removeColumnCondition,
    applyAdvancedFilter,
    clearFilterState,
    resetAllFilters,
    restoreQuickFilters,
    savedFilters,
    deleteFilter,
    saveNamedFilter,
    applySavedFilter,
    baseFilterValues,
    lockedColumns,
  };
}
